import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { PLATFORM_NAME } from '../config/brand';
import { copyrightLine } from '../config/brand';
import { nextFromSearch, consumeNextPath, registerPathWithNext } from '../utils/authRedirect';
import '../styles/Auth.css';

const StudentLoginPage = () => {
  const location = useLocation();
  const justVerified = location.state?.verified;
  const postAuthNext = nextFromSearch(location.search);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationHint, setShowVerificationHint] = useState(false);
  const [successBanner, setSuccessBanner] = useState(
    justVerified ? 'Email verified! You can now log in.' : ''
  );
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowVerificationHint(false);
    setSuccessBanner('');
    setLoading(true);

    const result = await login({ email, password });

    if (result.success) {
      navigate(consumeNextPath(postAuthNext || '/dashboard'), { replace: true });
    } else {
      setError(result.error);
      if (result.error && result.error.toLowerCase().includes('verify your email')) {
        setShowVerificationHint(true);
      }
    }

    setLoading(false);
  };

  const handleVerifyRedirect = () => {
    navigate(`/verify-email?email=${encodeURIComponent(email)}`);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/newlogo.png" alt={PLATFORM_NAME} className="auth-brand-logo" />
          <h1>Sign in</h1>
          <p>Continue to your assessments</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {successBanner && (
            <div className="success-message" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#166534', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#16a34a" opacity="0.15"/>
                <path d="M6 10l3 3 5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {successBanner}
            </div>
          )}

          {error && (
            <div className={`error-message ${showVerificationHint ? 'error-message-warning' : ''}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="currentColor"/>
              </svg>
              <div>
                <span>{error}</span>
                {showVerificationHint && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      type="button"
                      className="resend-link-btn"
                      onClick={handleVerifyRedirect}
                    >
                      Verify your email →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); setSuccessBanner(''); }}
              placeholder="Enter your email"
              required
              autoFocus
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <div className="forgot-password-link">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Logging in…
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to={registerPathWithNext(postAuthNext)}>Register here</Link>
          </p>
          <p className="auth-footer-secondary">
            <Link to="/" className="back-to-landing">← Back to home</Link>
          </p>
          <p style={{ marginTop: '16px', fontSize: '12px', color: '#718096' }}>
            {copyrightLine()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentLoginPage;
